import { useState } from 'react';
import { useAccessLogs, AccessAlert, DistItem, DailyAccessPoint } from '../../../../hooks/useAccessLogs';
import { AccessTable } from './AccessTable';
import { LoginLogTable } from './LoginLogTable';
import type { PeriodFilter } from '../../../../hooks/useExecutiveData';

interface Props {
  period: PeriodFilter;
}

// ─── Sub-components ────────────────────────────────────────────────────────

const KPICard = ({
  icon, label, value, sub, color,
}: { icon: string; label: string; value: string | number; sub?: string; color: string }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3 hover:bg-white/[0.07] transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-white/40 leading-tight pr-2">{label}</span>
      <div className={`w-8 h-8 flex items-center justify-center rounded-xl shrink-0 ${color}`}>
        <i className={`${icon} text-[15px] text-white`} />
      </div>
    </div>
    <div>
      <p className="text-[26px] font-700 text-white leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-white/30 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const HorizBar = ({
  label, count, pct, color = 'bg-violet-500/70',
}: { label: string; count: number; pct: number; color?: string }) => (
  <div className="flex items-center gap-3 py-1.5">
    <span className="text-[12px] text-white/50 w-28 shrink-0 truncate" title={label}>{label}</span>
    <div className="flex-1 h-4 bg-white/5 rounded-md overflow-hidden">
      <div
        className={`h-full ${color} rounded-md transition-all duration-500`}
        style={{ width: `${Math.max(pct, count > 0 ? 5 : 0)}%` }}
      />
    </div>
    <span className="text-[12px] font-600 text-white/70 w-6 text-right shrink-0">{count}</span>
    <span className="text-[11px] text-white/25 w-8 text-right shrink-0">{pct}%</span>
  </div>
);

const AlertBanner = ({ alert }: { alert: AccessAlert }) => {
  const cls = {
    danger:  { bg: 'bg-red-500/10',    border: 'border-red-500/30',    text: 'text-red-400'    },
    warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' },
    info:    { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400' },
    success: { bg: 'bg-emerald-500/10',border: 'border-emerald-500/30',text: 'text-emerald-400'},
  }[alert.type];

  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border ${cls.bg} ${cls.border}`}>
      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${cls.bg} shrink-0 mt-0.5`}>
        <i className={`${alert.icon} text-[15px] ${cls.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-[13px] font-600 ${cls.text}`}>{alert.title}</p>
          {alert.value && (
            <span className={`text-[11px] font-700 px-2 py-0.5 rounded-full ${cls.bg} ${cls.text} border ${cls.border}`}>
              {alert.value}
            </span>
          )}
        </div>
        <p className="text-[12px] text-white/40 mt-0.5 leading-relaxed">{alert.description}</p>
      </div>
    </div>
  );
};

const DailyChart = ({ trend }: { trend: DailyAccessPoint[] }) => {
  const maxSessions = Math.max(...trend.map((d) => d.sessions), 1);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[13px] font-600 text-white flex items-center gap-2">
          <i className="ri-line-chart-line text-cyan-400 text-[14px]" />
          Sessões por dia (últimos 30 dias)
        </h3>
      </div>
      <p className="text-[11px] text-white/30 mb-4">Barras = sessões totais · passe o mouse para detalhes</p>
      <div className="flex items-end gap-px h-28 overflow-hidden">
        {trend.map((d) => {
          const h = maxSessions > 0 ? Math.round((d.sessions / maxSessions) * 100) : 0;
          const isToday = d.date === today;
          return (
            <div
              key={d.date}
              className="flex-1 group relative flex flex-col justify-end"
              title={`${d.date.slice(5)}: ${d.sessions} sessões · ${d.uniqueUsers} usuário${d.uniqueUsers !== 1 ? 's' : ''}`}
            >
              <div
                className={`w-full rounded-t-sm transition-all cursor-default ${
                  isToday ? 'bg-cyan-400' : 'bg-violet-500/50 group-hover:bg-violet-400/80'
                }`}
                style={{ height: `${Math.max(h, d.sessions > 0 ? 4 : 0)}%` }}
              />
              {/* Tooltip */}
              <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 bg-[#13131f] border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity shadow-lg">
                <p className="font-600">{d.date.slice(5)}</p>
                <p className="text-white/60">{d.sessions}s · {d.uniqueUsers}u</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/20">
        <span>{trend[0]?.date?.slice(5) ?? ''}</span>
        <span className="text-cyan-400/60">Hoje</span>
      </div>
    </div>
  );
};

const DistColumns = ({ deviceDist, browserDist, osDist }: {
  deviceDist: DistItem[];
  browserDist: DistItem[];
  osDist: DistItem[];
}) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <h3 className="text-[13px] font-600 text-white mb-4 flex items-center gap-2">
        <i className="ri-computer-line text-cyan-400 text-[14px]" />
        Dispositivos
      </h3>
      {deviceDist.length === 0
        ? <p className="text-[12px] text-white/25">Sem dados</p>
        : deviceDist.map((d) => <HorizBar key={d.label} label={d.label} count={d.count} pct={d.pct} color="bg-cyan-500/60" />)
      }
    </div>
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <h3 className="text-[13px] font-600 text-white mb-4 flex items-center gap-2">
        <i className="ri-global-line text-violet-400 text-[14px]" />
        Navegadores
      </h3>
      {browserDist.length === 0
        ? <p className="text-[12px] text-white/25">Sem dados</p>
        : browserDist.map((d) => <HorizBar key={d.label} label={d.label} count={d.count} pct={d.pct} color="bg-violet-500/60" />)
      }
    </div>
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <h3 className="text-[13px] font-600 text-white mb-4 flex items-center gap-2">
        <i className="ri-settings-4-line text-emerald-400 text-[14px]" />
        Sistemas Operacionais
      </h3>
      {osDist.length === 0
        ? <p className="text-[12px] text-white/25">Sem dados</p>
        : osDist.map((d) => <HorizBar key={d.label} label={d.label} count={d.count} pct={d.pct} color="bg-emerald-500/60" />)
      }
    </div>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────

export const AccessSection = ({ period }: Props) => {
  const { data, loading, refetch } = useAccessLogs(period);
  const [activeView, setActiveView] = useState<'overview' | 'table' | 'loginlog'>('overview');

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <i className="ri-loader-4-line text-2xl text-cyan-400 animate-spin" />
        <p className="text-white/40 text-[13px]">Carregando logs de acesso...</p>
      </div>
    );
  }

  const { kpis, devices, dailyTrend, hourlyDist, alerts, plans, userTable } = data;
  const maxHourly = Math.max(...hourlyDist.map((h) => h.count), 1);
  const pctAccessed = kpis.totalUsers > 0
    ? Math.round((kpis.usersAccessedInPeriod / kpis.totalUsers) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Sub-nav + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'overview',  label: 'Visão Geral',        icon: 'ri-bar-chart-2-line' },
          { id: 'table',     label: 'Tabela de Usuários', icon: 'ri-table-line'        },
          { id: 'loginlog',  label: 'Log de Logins',      icon: 'ri-login-box-line'    },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id as 'overview' | 'table' | 'loginlog')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-500 whitespace-nowrap cursor-pointer transition-all border ${
              activeView === v.id
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                : 'text-white/40 hover:bg-white/5 hover:text-white border-transparent'
            }`}
          >
            <i className={`${v.icon} text-[13px]`} />
            {v.label}
            {v.id === 'loginlog' && data.loginLog.length > 0 && (
              <span className="ml-1 text-[10px] font-700 bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded-full">
                {data.loginLog.length}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={refetch}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-white/40 hover:text-white/70 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-refresh-line text-[13px]" />
          Atualizar
        </button>
      </div>

      {activeView === 'overview' && (
        <>
          {/* Engagement meter */}
          <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4 flex items-center gap-5 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <p className="text-[12px] text-white/40 mb-1">Taxa de acesso no período selecionado</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-cyan-500 rounded-full transition-all duration-700"
                    style={{ width: `${pctAccessed}%` }}
                  />
                </div>
                <span className="text-[20px] font-700 text-white shrink-0">{pctAccessed}%</span>
              </div>
              <p className="text-[11px] text-white/30 mt-1">
                {kpis.usersAccessedInPeriod} de {kpis.totalUsers} usuários acessaram
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0 flex-wrap">
              {[
                { label: 'Recorrentes', value: kpis.recurringUsers, color: 'text-emerald-400' },
                { label: 'Novos (1º acesso)', value: kpis.newUsersFirstAccess, color: 'text-cyan-400' },
                { label: 'Média sessões/user', value: kpis.avgSessionsPerActiveUser, color: 'text-violet-400' },
              ].map((m) => (
                <div key={m.label} className="text-center px-3 border-l border-white/10">
                  <p className={`text-[22px] font-700 ${m.color}`}>{m.value}</p>
                  <p className="text-[11px] text-white/30 mt-0.5">{m.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* KPI row 1 — session counts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KPICard icon="ri-login-box-line"       label="Sessões no período"   value={kpis.totalSessionsInPeriod} sub={`${kpis.usersAccessedInPeriod} usuários ativos`} color="bg-violet-600" />
            <KPICard icon="ri-calendar-today-line"  label="Sessões hoje"         value={kpis.sessionsToday}         sub="últimas 24h"                                    color="bg-cyan-600"   />
            <KPICard icon="ri-time-line"            label="Sessões (7 dias)"     value={kpis.sessionsLast7d}        sub="últimos 7 dias"                                 color="bg-teal-600"   />
            <KPICard icon="ri-calendar-line"        label="Sessões (30 dias)"    value={kpis.sessionsLast30d}       sub="últimos 30 dias"                                color="bg-indigo-600" />
          </div>

          {/* KPI row 2 — inactivity */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <p className="text-[12px] text-white/30 font-500 flex items-center gap-1.5">
                <i className="ri-information-line text-[13px]" />
                Inativos são calculados do momento atual — independente do filtro de período selecionado
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KPICard icon="ri-user-unfollow-line" label="Inativos há 7+ dias"  value={kpis.usersInactiveOver7d}  sub="sem acesso há 7 dias"   color="bg-amber-600"  />
              <KPICard icon="ri-user-unfollow-line" label="Inativos há 15+ dias" value={kpis.usersInactiveOver15d} sub="sem acesso há 15 dias"  color="bg-orange-600" />
              <KPICard icon="ri-user-unfollow-line" label="Inativos há 30+ dias" value={kpis.usersInactiveOver30d} sub="alto risco de churn"     color="bg-red-600"    />
              <KPICard icon="ri-user-add-line"      label="1º acesso no período" value={kpis.newUsersFirstAccess}  sub="estrearam no sistema"   color="bg-pink-600"   />
            </div>
          </div>

          {/* Device / Browser / OS */}
          <DistColumns
            deviceDist={devices.deviceDist}
            browserDist={devices.browserDist}
            osDist={devices.osDist}
          />

          {/* Daily trend */}
          <DailyChart trend={dailyTrend} />

          {/* Hourly distribution */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <h3 className="text-[13px] font-600 text-white mb-1 flex items-center gap-2">
              <i className="ri-time-line text-amber-400 text-[14px]" />
              Distribuição de acessos por hora do dia
            </h3>
            <p className="text-[11px] text-white/30 mb-4">
              Horários com maior volume de sessões (baseado na hora local do servidor)
            </p>
            <div className="flex items-end gap-px h-20 overflow-hidden">
              {hourlyDist.map((h) => {
                const ht = maxHourly > 0 ? Math.round((h.count / maxHourly) * 100) : 0;
                const isPeak = ht >= 70;
                return (
                  <div
                    key={h.hour}
                    className="flex-1 group relative flex flex-col justify-end"
                    title={`${String(h.hour).padStart(2, '0')}h: ${h.count} acesso${h.count !== 1 ? 's' : ''}`}
                  >
                    <div
                      className={`w-full rounded-t-sm transition-all cursor-default ${
                        isPeak ? 'bg-amber-400' : 'bg-amber-500/30 group-hover:bg-amber-400/60'
                      }`}
                      style={{ height: `${Math.max(ht, h.count > 0 ? 8 : 0)}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/25">
              {[0, 3, 6, 9, 12, 15, 18, 21, 23].map((h) => (
                <span key={h}>{String(h).padStart(2, '0')}h</span>
              ))}
            </div>
          </div>

          {/* Alerts */}
          <div>
            <h3 className="text-[14px] font-600 text-white mb-3 flex items-center gap-2">
              <i className="ri-radar-line text-violet-400 text-[15px]" />
              Alertas de Acesso
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {alerts.map((a, i) => <AlertBanner key={i} alert={a} />)}
            </div>
          </div>
        </>
      )}

      {activeView === 'table' && (
        <AccessTable userTable={userTable} plans={plans} />
      )}

      {activeView === 'loginlog' && (
        <LoginLogTable loginLog={data.loginLog} />
      )}
    </div>
  );
};
